import { GenericResponse } from 'src/js/model/api/response/generic-response';
import { ipv6Regex } from 'src/js/validator/ip-validator-helper';
import { AudioAlgorithmEnum, AudioEncoderMode } from '../audio-encoder/audio-encoder-models';

export enum StreamEncapsulation {
  UDP = 2,
  RTP = 3,
  SRT = 34,
  WHIP = 128,
}

export enum StreamEncapsulationWithoutSRT {
  UDP = 2,
  RTP = 3,
}

export enum SRTMode {
  CALLER = 0,
  LISTENER = 1,
  RENDEZVOUS = 2,
}

export enum SRTEncryption {
  SRT_ENCRYPTION_NONE = 0,
  SRT_ENCRYPTION_128BIT = 16,
  SRT_ENCRYPTION_256BIT = 32,
}

export enum StreamShaping {
  VBR = 'vbr',
  CVBR = 'cvbr',
  CBR = 'cbr',
}

export enum SRTAuthentication {
  NONE = 'none',
  AUTO = 'auto',
  AES_GCM = 'AES-GCM',
}
export enum SRTAuthenticationListener {
  AUTO = 'auto',
  AES_GCM = 'AES-GCM',
}
export enum SRTAuthenticationOthers {
  NONE = 'none',
  AES_GCM = 'AES-GCM',
}

export enum NetworkInterfaces {
  auto = 'auto',
  eth0 = 'eth0',
  eth1 = 'eth1',
}

export enum StreamPublishingMode {
  standard = 'standard',
  custom = 'custom',
}

export enum FecRTP {
  DISABLED = 0,
  MPEG_PRO_FEC = 2,
}

export enum MpegProFecLevel {
  COLUMNS_ONLY = 0,
  ROW_AND_COLUMNS = 1,
}

export enum StreamState {
  STOPPED = 0,
  RUNNING,
  FAILED,
  CONNECTING,
  SECURING /* Acquiring keys for protected content */,
  LISTENING,
  PAUSED,
  PUBLISHING /* For RTMP after connection is established */,
  RESOLVING /* DNS resolution of host name to IP address in progress */,
  SCRAMBLED /* Stream encrypted and cannot decrypt */,
  CONNECTED /* Steady state for backup link of Active-Backup SRT redundant stream */,
  INITIALIZING /* used in WHIP */,
}

export enum SrtPathRedundancyMode {
  NONE = 'none',
  OPTIONAL = 'optional',
  ACTIVE_ACTIVE = 'active',
  ACTIVE_BACKUP = 'backup',
}

export enum SrtPathRedundancyModeOffered {
  NONE = 'none',
  ACTIVE_ACTIVE = 'active',
  ACTIVE_BACKUP = 'backup',
}

export enum SrtRedundancyPathRole {
  PRIMARY = 'primary',
  SECONDARY = 'secondary',
}

export enum MetadataEncapsulation {
  SYNC = 0 /* Sync Metadata (AU header) in Metadata PES (ISO/IEC 13818-1), default  */,
  ASYNC /* Async Metadata in Private Data PES (MISB 6004.2) */,
  ASYNC_SYNC_AU /* Sync AU in Async Metadata PES&PMT */,
}

interface SRTStats {
  pathName?: string;
  state: StreamState;
  srtRedundancyMode: SrtPathRedundancyMode;
  detectedRedundancyMode: SrtPathRedundancyMode;
  resentPackets: string;
  resentBytes: string;
  droppedPackets: string;
  droppedBytes: string;
  latency: number;
  localPort: number;
  remotePort: number;
  maxBandwidth: string;
  pathMaxBandwidth: string;
  lostPackets: string;
  localVersion: string;
  peerVersion: string;
  localEndpoint: string;
  peerEndpoint: string;
  buffer: number;
  reconnections: string;
  remoteAddress: string;
  mss: number;
  rtt: number;
  rrtWithUnits: string /* view model only */;
  recvACK: string;
  recvNAK: string;
  encryption?: string;
  encryptionMode?: string;
  keyLength?: string;
  authentication?: string;
  peerDecryption?: string;
  decryptError?: string;
  rejectedLastTime: string;
  rejectedReason: string;
  rejectedStreamId: string;
  path2: {
    pathName?: string;
    state: StreamState;
    reconnections: string;
    resentPackets: string;
    resentBytes: string;
    droppedPackets: string;
    droppedBytes: string;
    recoveredPackets: string;
    lostPackets: string;
    localVersion: string;
    peerVersion: string;
    localEndpoint: string;
    peerEndpoint: string;
    recvACK: string;
    recvNAK: string;
    mss: number;
    maxBandwidth: string;
    pathMaxBandwidth: string;
    sourceAddress: string;
    remoteAddress: string;
    localAddress: string;
    peerAddress: string;
    localPort: number;
    peerPort: number;
    remotePort: number;
    rtt: number;
    rrtWithUnits: string /* view model only */;
    buffer: number;
    latency: number;
    encryption?: string;
    encryptionMode?: string;
    keyLength?: string;
    authentication?: string;
    peerDecryption?: string;
    decryptError?: string;
  };
}

export interface StreamInfo {
  id: number;
  decoderId?: number;
  name?: string;
  type?: number; // unused
  bandwithEstimate?: number | string;
  encapsulation: StreamEncapsulation;
  metadataEncapsulation?: MetadataEncapsulation;
  metadataBandwidth?: number | string;
  metadataBandwidthAutoAssigned?: boolean;
  idleCells: boolean;
  delayedAudio: boolean;
  persistent: boolean;
  fec?: number; // unused
  mtu: number;
  tos: string;
  ttl: number;
  port: number | '';
  address?: string;
  url?: string; // for WHIP
  bearerToken?: string; // for WHIP
  sourceIp?: string;
  stillImage?: string;
  userData?: string;
  latency?: number;
  shaping?: StreamShaping;
  srtMode?: SRTMode;
  sourcePort?: number | '';
  strictMode?: boolean;
  passphrase?: string;
  passphraseSet?: boolean;
  adaptive?: boolean;
  encryption?: boolean;
  encKeyLength?: number;
  networkInterface?: string;
  overheadPercentage?: number;
  authentication?: SRTAuthentication;
  srtAccessControlPublishingIdType?: string;
  srtAccessPublishingID?: string;
  srtAccessUserName?: string;
  srtAccessResourceName?: string;
  srtRedundancyMode?: SrtPathRedundancyMode;
  srtRedundancyPath1Role?: string;
  srtRedundancyPath1Address?: string;
  srtRedundancyPath2Address?: string;
  srtRedundancyPath1Port?: number | '';
  srtRedundancyPath2Port?: number | '';
  srtRedundancyPath1SourcePort?: number | '';
  srtRedundancyPath2SourcePort?: number | '';
  srtRedundancyPath1Name?: string;
  srtRedundancyPath2Name?: string;
  srtRedundancyPath2NetworkInterface?: string;
  srtListenerSecondPort?: number | '';
  fecRtp?: FecRTP;
  level?: MpegProFecLevel;
  rows?: number;
  columns?: number;
  blockAligned?: boolean;
  sap?: {
    id?: number;
    advertise?: boolean;
    name?: string;
    desc?: string;
    author?: string;
    keywords?: string;
    copyright?: string;
    addressConfigured?: string;
    address?: string;
    port?: number;
  };
  audio?: {
    autoassigned?: boolean;
    channels?: number;
    codec?: AudioAlgorithmEnum;
    id: number;
    interface?: number;
    layout?: AudioEncoderMode;
    name?: string;
    pid?: number;
  }[];
  video?: { id: number; name?: string; autoassigned?: boolean; pid?: number; interface?: number }[];
  metadata?: {
    id: number;
    name?: string;
    autoassigned?: boolean;
    pid?: number;
    interface?: number;
  }[];
  scte?: { id: number; name?: string; autoassigned?: boolean; pid?: number; interface?: number }[];
  pcrPid?: number;
  pmtPid?: number;
  tsStreamId?: number;
  programNumber?: number;
  pmtPidAutoAssigned?: boolean;
  pcrPidAutoAssigned?: boolean;
  programNumberAutoAssigned?: boolean;
}

export interface StreamStats {
  state: StreamState;
  stateString: string;
  uptime: string;
  lastReset: string;
  sentPackets: string;
  sentBytes: string;
  bitrate: string;
  bitrateInKbps: number;
  destAddress?: string;
  duplicate?: string;
  rtmpLastError?: string;
  ssrc?: string;
  sourcePort?: number;
  unsentPackets?: string;
  unsentBytes?: string;
  lastError?: string;
  occurred?: string;
  /* rtcp only begins */
  rtcp?: string;
  rxOKRTCPPackets?: string;
  rxOKRTCPBytes?: string;
  txOKRTCPPackets?: string;
  txOKRTCPBytes?: string;
  RTCPReporters?: number;
  rxInvalidRTCPPackets?: string;
  rxInvalidRTCPBytes?: string;
  lastRxError?: string;
  txFailedRTCPPackets?: string;
  txFailedRTCPBytes?: string;
  lastTxError?: string;
  /* rtcp only ends */

  programNumber?: string;
  pcrPid?: string;
  sourceAddress?: string;
  connections?: string;
  fecRtp?: number;
  fecLevel?: string;
  fecRows?: string;
  fecColumns?: string;
  droppedPackets?: string;
  lostPackets?: string;
  corruptedFrames?: number;
  restarts?: string;
  srt?: SRTStats;
  sources?: [
    {
      name: string;
      compression: string;
      bitrate: string;
      programId: number;
      receivedPackets: string;
      receivedBytes: string;
      PTS: string;
      DTS: string;
    },
  ];
  whipLocalPort?: number;
  whipRemotePort?: number;
  whipFullIntraRequests?: string;
  whipPictureLoss?: string;
  whipNACK?: string;
  whipPacketLoss?: string;
  whipRTT?: string;
  whipJitter?: string;
  whipLastError?: string;
  whipLastErrorOccured?: string;
}

export interface StreamResponse extends GenericResponse {
  info: StreamInfo;
  stats: StreamStats;
}

export interface GetAllStreamsResponse {
  data: StreamResponse[];
}

export interface StreamViewModel extends StreamInfo {
  audioPids: string;
  videoPids: string;
  metadataPids: string;
  sctePids: string;
  audioPidsPlaceholder?: string;
  videoPidsPlaceholder?: string;
  metadataPidsPlaceholder?: string;
  sctePidsPlaceholder?: string;
  pcrPidPlaceholder?: string;
  pmtPidPlaceholder?: string;
  programNumberPlaceholder?: string;
  pcrPid?: number;
  pmtPid?: number;
  tsStreamId?: number;
  programNumber?: number;
  tsStreamIdPlaceholder?: string;
  metadataBandwidthPlaceholder?: string;
  sapAddressPlaceholder?: string;

  destination: string;
  encrypted: boolean;
  stateString: string;
  statusIcon: string;
  stats: Partial<StreamStats>;
  encapsulationTextual: string;

  videoOutputResolution: string;
  videoBitrate: string;
  audioBitrate: string;

  codecAlgorithm?: string; // comes from videoEncoder
  previewIntervalSec: number;
  srtStreamId: boolean; // pure UI helper variable
  stateSortable: number;
  state: StreamState;
  selected: boolean /* checked by checkbox */;
  hidden: boolean /* when filtered out */;
  updated: string /* to trigger setState to detect change and re-render */;
}

export const getStreamUuid = (model: StreamViewModel | StreamInfo) => {
  if (model.userData?.length > 0) {
    return model.userData;
  }
  return model.id; // for old streams or streams created by CLI and other interfaces
};

export const streamIsStopped = (state: StreamState): boolean => state === StreamState.STOPPED;

export const streamIsStarted = (state: StreamState): boolean => state !== StreamState.STOPPED;

const getDestinationShorthandRightSide = (model: StreamViewModel) => {
  if (
    model.stats != null &&
    (model.srtRedundancyMode === 'active' || model.srtRedundancyMode === 'backup') && // active-active or active-backup
    Number(model.srtRedundancyPath2Port) > 0 &&
    model.stats.srt != null &&
    model.stats.srt.state !== StreamState.RUNNING /*NOT STREAMING*/ &&
    model.stats.srt.path2 != null &&
    model.stats.srt.path2.state === StreamState.RUNNING /*STREAMING*/
  ) {
    // feed from path2 since this one is connected and path1 is not
    return ':' + model.srtRedundancyPath2Port;
  } else if (model.port > 0 && model.srtRedundancyMode === 'optional' && Number(model.srtListenerSecondPort) > 0) {
    return ':' + model.port + ', ' + model.srtListenerSecondPort;
  } else if (model.port > 0) {
    return ':' + model.port;
  }
  return '';
};

export const getDestinationShorthand = (model: StreamViewModel) => {
  let address = model.address;
  if (model.encapsulation === StreamEncapsulation.WHIP) {
    address = model.url;
  }

  if (
    model.stats != null &&
    (model.srtRedundancyMode === 'active' || model.srtRedundancyMode === 'backup') && // active-active or active-backup
    Number(model.srtRedundancyPath2Port) > 0 &&
    model.stats.srt != null &&
    model.stats.srt.state !== StreamState.RUNNING /*NOT STREAMING*/ &&
    model.stats.srt.path2 != null &&
    model.stats.srt.path2.state === StreamState.RUNNING /*STREAMING*/
  ) {
    // feed from path2 since this one is active
    address = model.srtRedundancyPath2Address;
  } else if (
    (address === 'Any' || address === '0.0.0.0') &&
    model.stats != null &&
    model.stats.srt != null &&
    model.stats.srt.remoteAddress != null &&
    model.stats.srt.remoteAddress.length > 0
  ) {
    address = model.stats.srt.remoteAddress;
  }

  if (
    (address === 'Any' || address === '0.0.0.0') &&
    model.stats != null &&
    model.stats.srt != null &&
    model.stats.srt.remoteAddress != null &&
    (model.stats.srt.remoteAddress === 'Any' || model.stats.srt.remoteAddress === '0.0.0.0') &&
    model.srtRedundancyMode === 'optional' && // listener mode.
    model.stats.srt.path2 != null &&
    model.stats.srt.path2.remoteAddress != null &&
    model.stats.srt.path2.remoteAddress !== 'Any' &&
    model.stats.srt.path2.remoteAddress !== '0.0.0.0' &&
    model.stats.srt.path2.remoteAddress.length > 0
  ) {
    address = model.stats.srt.path2.remoteAddress; // feed from path2 if path1 has not remote address
  }

  if (address && ipv6Regex.exec(address)) {
    address = '[' + address + ']';
  }
  return address + getDestinationShorthandRightSide(model);
};

export const isEmptyContentSource = (source: any) => {
  if (source == null) {
    return true;
  }
  if (Array.isArray(source) && source.length === 0) {
    return true;
  }
  if (Array.isArray(source) && source.length === 1 && source[0].id == -1) {
    return true;
  }
  return false;
};
